import { useState, useEffect, useRef, useMemo } from 'react';
import { getPresetDisplayName } from '../utils/presets';
import { isPopupOpen } from '../utils/popupUtils';
import { generateClientSideId } from '../utils/clientIdGenerator';
import { usePresetTasks } from './usePresetTasks';
import { useTaskMutations } from './useTaskMutations';
import { useFilesCrud } from './useFilesCrud';
import { ensureTasksHaveReactKeys } from '../../features/tasks/utils/taskStateHelpers';

const LOGGED_OUT_DRAFT_KEY = 'taskdown_logged_out_draft';
const LOGGED_OUT_MAX_LISTS = 3;
const LOGGED_OUT_MAX_TASKS = 20;

function normalizeLegacySingleListDraft(data) {
  if (!data || typeof data.filename !== 'string' || !Array.isArray(data.tasks)) return null;
  const defaultFileId = 'default';
  return {
    version: 2,
    currentFileId: defaultFileId,
    files: [{ id: defaultFileId, name: data.filename }],
    tasksByFileId: { [defaultFileId]: data.tasks },
  };
}

function getLoggedOutWorkspaceDraft() {
  try {
    const raw = localStorage.getItem(LOGGED_OUT_DRAFT_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data && Array.isArray(data.files) && data.tasksByFileId && typeof data.tasksByFileId === 'object') {
      return data;
    }
    return normalizeLegacySingleListDraft(data);
  } catch (_) {}
  return null;
}

function setLoggedOutWorkspaceDraft({ files, tasksByFileId, currentFileId }) {
  try {
    localStorage.setItem(
      LOGGED_OUT_DRAFT_KEY,
      JSON.stringify({
        version: 2,
        files,
        tasksByFileId,
        currentFileId,
      })
    );
  } catch (_) {}
}

export function clearLoggedOutDraft() {
  try {
    localStorage.removeItem(LOGGED_OUT_DRAFT_KEY);
  } catch (_) {}
}

export function useTasks(isLoggedIn = false, isLoading = true, presetSlug = null, listIdFromUrl = null, fromSidebarNavigation = false) {
  const [tasks, setTasks] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFileId, setCurrentFileId] = useState(null);
  const [listAccessDenied, setListAccessDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [hasRestoredDraft, setHasRestoredDraft] = useState(false);
  const originalTasksRef = useRef([]);
  const pendingChangesRef = useRef({
    modified: new Map(),
    created: [],
    deleted: [],
  });
  const saveTimeoutRef = useRef(null);
  const pendingFocusTaskRef = useRef(null);
  const [pendingFocusTrigger, setPendingFocusTrigger] = useState(0);
  const fileTasksCacheRef = useRef(new Map());
  const fileOriginalTasksCacheRef = useRef(new Map());
  const updatedLastOpenedForListIdRef = useRef(null);
  /** After the first completed logged-in tasks fetch, file switches fetch in the background without full-screen loading. */
  const loggedInBlockingTaskLoadDoneRef = useRef(false);
  const isLoggedInRef = useRef(isLoggedIn);
  isLoggedInRef.current = isLoggedIn;
  const presetTitle = presetSlug ? getPresetDisplayName(presetSlug) : null;

  const { getPresetTasks } = usePresetTasks();

  const applyLoggedOutFiles = () => {
    if (presetSlug && presetTitle) {
      setFiles([
        { id: 'preset', name: presetTitle }
      ]);
      setCurrentFileId('preset');
      return;
    }

    setFiles([
      { id: 'default', name: 'My todolist' }
    ]);
    if (currentFileId === null) {
      setCurrentFileId('default');
    }
  };

  const fetchFiles = async () => {
    if (!isLoggedIn) {
      // Require login to view a list by URL id
      if (listIdFromUrl != null && listIdFromUrl !== '') {
        setListAccessDenied(true);
        return;
      }
      // Restore from localStorage if user had a draft
      const draft = getLoggedOutWorkspaceDraft();
      if (draft) {
        const restoredFiles = (draft.files || []).filter((f) => f && f.id != null && typeof f.name === 'string');
        const filesToApply = restoredFiles.length > 0 ? restoredFiles : [{ id: 'default', name: 'My todolist' }];
        setFiles(filesToApply);
        const initialFileId = draft.currentFileId ?? filesToApply[0]?.id ?? 'default';
        setCurrentFileId(initialFileId);
        const tasksByFileId = draft.tasksByFileId || {};
        filesToApply.forEach((file) => {
          const fileTasks = Array.isArray(tasksByFileId[file.id]) ? tasksByFileId[file.id] : [];
          const tasksWithKeys = ensureTasksHaveReactKeys(fileTasks);
          const tasksCopy = JSON.parse(JSON.stringify(tasksWithKeys));
          fileTasksCacheRef.current.set(file.id, tasksCopy);
          fileOriginalTasksCacheRef.current.set(file.id, JSON.parse(JSON.stringify(tasksCopy)));
        });
        setHasRestoredDraft(true);
        return;
      }
      applyLoggedOutFiles();
      return;
    }
    
    try {
      const response = await fetch('/api/files.php');
      if (response.ok) {
        const data = await response.json();
        setFiles(data);

        // If URL has a list id, use it only if the user owns that file (it's in their list).
        // Use the file's actual id from the API so type matches task.file_id (number) for filtering.
        if (listIdFromUrl != null && listIdFromUrl !== '') {
          const idStr = String(listIdFromUrl);
          const found = data.find(f => String(f.id) === idStr);
          if (found) {
            setCurrentFileId(found.id);
            setListAccessDenied(false);
          } else {
            setCurrentFileId(null);
            setListAccessDenied(true);
          }
          return;
        }

        setListAccessDenied(false);

        // If no current file is set, try to get the last opened file from user info
        if (currentFileId === null) {
          try {
            const userResponse = await fetch('/api/user.php');
            if (userResponse.ok) {
              const userData = await userResponse.json();
              const lastOpenedFileId = userData.last_opened_file_id;
              
              // Check if the last opened file still exists in the files list
              if (lastOpenedFileId) {
                const lastOpenedFile = data.find(f => f.id === lastOpenedFileId);
                if (lastOpenedFile) {
                  setCurrentFileId(lastOpenedFileId);
                  return;
                }
              }
            }
          } catch (userError) {
            console.error('Error fetching user info:', userError);
          }
          
          // Fallback to first file (or todolist.td if it exists)
          const defaultFile = data.find(f => f.name === 'todolist.td') || data[0];
          if (defaultFile) {
            setCurrentFileId(defaultFile.id);
          }
        }
      } else if (response.status === 401) {
        // Not authenticated - show default file
        applyLoggedOutFiles();
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      if (!isLoggedIn) {
        applyLoggedOutFiles();
      } else {
        setFiles([]);
      }
    }
  };

  const fetchTasks = async (fileId = null) => {
    if (!isLoggedIn) {
      // Check if we have cached tasks for this file
      const cachedTasks = fileTasksCacheRef.current.get(fileId);
      const cachedOriginalTasks = fileOriginalTasksCacheRef.current.get(fileId);

      if (cachedTasks !== undefined) {
        // Restore from cache. Do not clear pendingChangesRef so that when switching back to a
        // previously edited file, it still shows as having unsaved changes (Save button visible).
        setTasks(cachedTasks);
        originalTasksRef.current = cachedOriginalTasks ? JSON.parse(JSON.stringify(cachedOriginalTasks)) : JSON.parse(JSON.stringify(cachedTasks));
        setLoading(false);
        return;
      }

      // No cache; show loading while we fetch
      setLoading(true);

      // No cache, fetch new tasks
      if (fileId === 'preset' && presetSlug) {
        const presetTasks = await getPresetTasks('preset', presetSlug);
        setTasks(presetTasks);
        const originalPresetTasks = JSON.parse(JSON.stringify(presetTasks));
        originalTasksRef.current = originalPresetTasks;
        // Cache the preset tasks
        fileTasksCacheRef.current.set(fileId, presetTasks);
        fileOriginalTasksCacheRef.current.set(fileId, originalPresetTasks);
      } else if (fileId && fileId.startsWith('preset:')) {
        const slug = fileId.slice('preset:'.length);
        const presetTasks = await getPresetTasks(fileId, slug);
        setTasks(presetTasks);
        const originalPresetTasks = JSON.parse(JSON.stringify(presetTasks));
        originalTasksRef.current = originalPresetTasks;
        fileTasksCacheRef.current.set(fileId, presetTasks);
        fileOriginalTasksCacheRef.current.set(fileId, originalPresetTasks);
      } else {
        // Show empty tasks when not authenticated
        const emptyTasks = [];
        setTasks(emptyTasks);
        originalTasksRef.current = [];
        // Cache empty tasks
        fileTasksCacheRef.current.set(fileId, emptyTasks);
        fileOriginalTasksCacheRef.current.set(fileId, []);
      }
      // Do not clear pendingChangesRef when switching files (logged out), so that switching
      // back to a previously edited file still shows the Save button.
      setLoading(false);
      return;
    }

    // Logged in: full-screen loading only until the first fetch finishes; later file switches stay mounted.
    const useBlockingLoad = !loggedInBlockingTaskLoadDoneRef.current;
    if (useBlockingLoad) {
      setLoading(true);
    }

    try {
      const url = fileId ? `/api/tasks.php?file_id=${fileId}` : '/api/tasks.php';
      const response = await fetch(url);
      const text = await response.text();
      if (response.ok) {
        try {
          const data = JSON.parse(text);
          // Ensure all tasks have a _reactKey (generate unique client-side ID)
          const tasksWithKeys = data.map(task => ({
            ...task,
            _reactKey: task._reactKey || generateClientSideId()
          }));
          setTasks(tasksWithKeys);
          originalTasksRef.current = JSON.parse(JSON.stringify(tasksWithKeys)); // Deep copy
          pendingChangesRef.current = {
            modified: new Map(),
            created: [],
            deleted: []
          };
          setHasPendingChanges(false);
          
          // If there's a pending task to focus, trigger effect to focus after commit
          if (pendingFocusTaskRef.current) {
            setPendingFocusTrigger((n) => n + 1);
          }
        } catch (parseError) {
          console.error('Error parsing JSON:', parseError);
          console.error('Response text:', text);
        }
      } else if (response.status === 401) {
        // Not authenticated - show empty tasks
        setTasks([]);
        originalTasksRef.current = [];
        pendingChangesRef.current = {
          modified: new Map(),
          created: [],
          deleted: []
        };
        setHasPendingChanges(false);
        setLoading(false);
      } else {
        console.error('Error response:', response.status, text);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setTasks([]);
      if (!isLoggedIn) {
        originalTasksRef.current = [];
        pendingChangesRef.current = {
          modified: new Map(),
          created: [],
          deleted: []
        };
        setHasPendingChanges(false);
      }
    } finally {
      setLoading(false);
      if (useBlockingLoad && isLoggedInRef.current) {
        loggedInBlockingTaskLoadDoneRef.current = true;
      }
    }
  };

  useEffect(() => {
    if (!isLoggedIn) {
      loggedInBlockingTaskLoadDoneRef.current = false;
    }
  }, [isLoggedIn]);

  useEffect(() => {
    // Wait for auth check to complete before making requests
    if (!isLoading) {
      fetchFiles();
    }
  }, [isLoggedIn, isLoading, presetSlug, listIdFromUrl]);

  useEffect(() => {
    // Wait for auth check to complete before making requests
    if (!isLoading && (currentFileId !== null || !isLoggedIn)) {
      fetchTasks(currentFileId);
    }
  }, [currentFileId, isLoggedIn, isLoading, presetSlug]);

  // Focus task after fetch/save when pendingFocusTaskRef is set (React-driven, no setTimeout)
  useEffect(() => {
    if (pendingFocusTrigger === 0 || !pendingFocusTaskRef.current) return;
    const pending = pendingFocusTaskRef.current;
    if (isPopupOpen()) {
      pendingFocusTaskRef.current = null;
      setPendingFocusTrigger(0);
      return;
    }
    const matchingTask = tasks.find(
      (t) =>
        t.text === pending.text &&
        (t.parent_task_id === pending.parentTaskId ||
          (pending.parentTaskId === null && t.parent_task_id === null)) &&
        t.order === pending.order
    );
    if (matchingTask) {
      const taskElement = document.querySelector(
        `.task-text[data-task-id="${matchingTask.id}"]`
      );
      if (taskElement) {
        taskElement.focus();
        const range = document.createRange();
        range.selectNodeContents(taskElement);
        range.collapse(false);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        window.dispatchEvent(
          new CustomEvent('taskFocused', { detail: { taskId: matchingTask.id } })
        );
      }
    }
    pendingFocusTaskRef.current = null;
    setPendingFocusTrigger(0);
  }, [tasks, pendingFocusTrigger]);

  // Declare saveAllChanges first so it can be referenced by triggerAutoSave
  const saveAllChanges = async () => {
    // Don't save if not logged in
    if (!isLoggedIn) {
      return;
    }
    
    // Clear any pending save timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    // Check if there are actually pending changes in the ref (more reliable than state)
    const { modified, created, deleted } = pendingChangesRef.current;
    const hasActualChanges = modified.size > 0 || created.length > 0 || deleted.length > 0;
    
    // If there are no pending changes, don't save
    if (!hasActualChanges) {
      return;
    }
    
    try {
      
      // Process deletions first
      for (const taskId of deleted) {
        try {
          const response = await fetch('/api/tasks.php', {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ id: taskId })
          });
          if (!response.ok) {
            console.error('Error deleting task:', taskId);
          }
        } catch (error) {
          console.error('Error deleting task:', error);
        }
      }

      // Process creations
      // First, identify which parent_task_ids are temporary (from created tasks)
      const tempParentIds = new Set(created.map(t => t.id));
      
      const createdTaskMap = new Map();
      const taskUpdates = new Map(); // Map of _reactKey -> updated task data
      
      // First, read all DOM text values before any state updates
      const domTextMap = new Map(); // Map of _reactKey -> current DOM text
      for (const task of created) {
        try {
          // Try to find element by _reactKey first (more stable), fallback to task.id
          const element = document.querySelector(`.task-text[data-react-key="${task._reactKey}"]`) ||
                         document.querySelector(`.task-text[data-task-id="${task.id}"]`);
          if (element && element.textContent !== null && element.textContent !== undefined) {
            const domText = element.textContent.trim();
            if (domText !== task.text) {
              domTextMap.set(task._reactKey, domText);
            }
          }
        } catch (err) {
          // Ignore DOM access errors
        }
      }
      
      // Create all tasks and collect updates
      for (const task of created) {
        try {
          // If parent is a temporary ID, set it to null initially (will be updated later)
          const parentTaskId = task.parent_task_id && tempParentIds.has(task.parent_task_id) 
            ? null 
            : (task.parent_task_id || null);
          
          const response = await fetch('/api/tasks.php', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              text: task.text,
              parent_task_id: parentTaskId,
              status: task.status,
              order: task.order !== null && task.order !== undefined ? task.order : null,
              prioritize_order: task.prioritize_order !== null && task.prioritize_order !== undefined ? task.prioritize_order : null,
              due_date: task.due_date || null,
              is_later_date: task.is_later_date === true,
              file_id: task.file_id || currentFileId
            })
          });
          if (response.ok) {
            const newTask = await response.json();
            createdTaskMap.set(task.id, newTask.id);
            
            // Collect update: preserve _reactKey and use DOM text if available
            const textToUse = domTextMap.get(task._reactKey) || newTask.text;
            taskUpdates.set(task._reactKey, {
              ...newTask,
              _reactKey: task._reactKey,
              text: textToUse,
              // POST response currently omits prioritize_order; preserve optimistic local value.
              prioritize_order:
                task.prioritize_order !== undefined ? task.prioritize_order : null,
              // Keep explicit later flag used by Prioritize grouping/display.
              is_later_date:
                task.is_later_date !== undefined
                  ? task.is_later_date
                  : (newTask.is_later_date === true)
            });
          }
        } catch (error) {
          console.error('Error creating task:', error);
        }
      }

      // Process modifications
      const modificationUpdates = new Map(); // Map of taskId -> updated task data
      for (const [taskId, taskData] of modified) {
        // Skip if this task was created (it will be updated after creation)
        if (createdTaskMap.has(taskId)) {
          continue;
        }
        
        try {
          // Ensure is_later_date is always included when due_date is being updated
          // This is critical because the API needs to know whether to set or clear the flag
          // Backend accepts true/false and converts to 0/1 for MySQL, returns true/false from MySQL
          if ('due_date' in taskData) {
            // Always include is_later_date when due_date is present, even if it's false
            // If not explicitly set or if it's undefined/null, default to false (clears any previous "Later" flag)
            if (!('is_later_date' in taskData) || taskData.is_later_date === undefined || taskData.is_later_date === null) {
              taskData.is_later_date = false;
            }
            // Ensure it's a boolean true/false (backend will convert to 0/1 for MySQL)
            taskData.is_later_date = taskData.is_later_date === true;
          }
          
          const response = await fetch('/api/tasks.php', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(taskData)
          });
          if (response.ok) {
            const text = await response.text();
            try {
              const updatedTask = JSON.parse(text);
              modificationUpdates.set(taskId, updatedTask);
            } catch (parseError) {
              console.error('Error parsing JSON response for task:', taskId, parseError);
              console.error('Response text:', text);
            }
          } else {
            const errorText = await response.text();
            console.error('Error updating task:', taskId, 'Status:', response.status);
            console.error('Error response:', errorText);
          }
        } catch (error) {
          console.error('Error updating task:', taskId, error);
        }
      }
      
      // Apply all task updates in a single batch to prevent unmounting
      if (taskUpdates.size > 0 || modificationUpdates.size > 0) {
        setTasks(prevTasks => {
          return prevTasks.map(t => {
            // Check if this task was created (by _reactKey)
            if (taskUpdates.has(t._reactKey)) {
              return taskUpdates.get(t._reactKey);
            }
            // Check if this task was modified (by id)
            if (modificationUpdates.has(t.id)) {
              const updatedTask = modificationUpdates.get(t.id);
              return { ...updatedTask, _reactKey: t._reactKey };
            }
            return t;
          });
        });
      }

      // Update any created tasks that reference other created tasks
      const parentReferenceUpdates = new Map(); // Map of _reactKey -> updated task data
      for (const task of created) {
        const newTaskId = createdTaskMap.get(task.id);
        if (!newTaskId) continue;

        // Check if parent was also created
        if (task.parent_task_id && createdTaskMap.has(task.parent_task_id)) {
          const newParentId = createdTaskMap.get(task.parent_task_id);
          try {
            const response = await fetch('/api/tasks.php', {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                id: newTaskId,
                parent_task_id: newParentId,
                text: task.text,
                due_date: task.due_date,
                is_later_date: task.is_later_date === true,
                status: task.status,
                order: task.order,
                prioritize_order: task.prioritize_order !== null && task.prioritize_order !== undefined ? task.prioritize_order : null
              })
            });
            if (response.ok) {
              const updatedTask = await response.json();
              parentReferenceUpdates.set(task._reactKey, updatedTask);
            } else {
              console.error('Error updating created task parent:', newTaskId);
            }
          } catch (error) {
            console.error('Error updating created task parent:', error);
          }
        }
      }
      
      // Apply parent reference updates in a single batch
      if (parentReferenceUpdates.size > 0) {
        setTasks(prevTasks => {
          return prevTasks.map(t => {
            if (parentReferenceUpdates.has(t._reactKey)) {
              const updatedTask = parentReferenceUpdates.get(t._reactKey);
              return { ...updatedTask, _reactKey: t._reactKey };
            }
            return t;
          });
        });
      }

      // No longer need to dispatch taskIdsUpdated event since we use clientIds (_reactKey) 
      // which don't change when temp IDs are converted to real IDs

      // Update originalTasksRef in setState; clear pending changes ref synchronously so that
      // callers (e.g. switchFile after import) do not see hasActualChanges and trigger a second save
      setTasks(currentTasks => {
        originalTasksRef.current = JSON.parse(JSON.stringify(currentTasks)); // Deep copy
        return currentTasks;
      });
      pendingChangesRef.current = {
        modified: new Map(),
        created: [],
        deleted: []
      };
      setHasPendingChanges(false);

      // Trigger effect to focus pending task after state has committed
      if (pendingFocusTaskRef.current) {
        setPendingFocusTrigger((n) => n + 1);
      }
    } catch (error) {
      console.error('Error saving changes:', error);
    }
  };

  const triggerAutoSave = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveAllChanges();
    }, 500);
  };

  const {
    updateTaskStatus,
    updateTaskTextLocal,
    updateTaskText,
    updateTaskDueDate,
    updateTaskOrder,
    updateTaskOrders,
    updateTaskParent,
    updateTaskParentWithText,
    updateTaskParentWithTextAndOrder,
    createTask,
    deleteTask,
    clearAllTasks,
  } = useTaskMutations({
    tasks,
    setTasks,
    currentFileId,
    pendingChangesRef,
    setHasPendingChanges,
    triggerAutoSave,
    isLoggedIn,
  });

  const {
    updateLastOpenedFile,
    createFile,
    updateFileName,
    toggleArchiveFile,
    updateFileOrders,
    updateFileArchiveStatus,
    saveFileProgressStatus,
  } = useFilesCrud({
    files,
    setFiles,
    currentFileId,
    setCurrentFileId,
    setTasks,
    isLoggedIn,
    tasks,
  });

  const switchFile = async (fileId, preloadedTasks = null) => {
    // When preloadedTasks is provided, use it instead of fetching (e.g. after import when logged out)
    if (preloadedTasks != null && Array.isArray(preloadedTasks)) {
      setCurrentFileId(fileId);
      setTasks(preloadedTasks);
      originalTasksRef.current = JSON.parse(JSON.stringify(preloadedTasks));
      pendingChangesRef.current = { modified: new Map(), created: [], deleted: [] };
      setHasPendingChanges(false);
      if (!isLoggedIn) {
        fileTasksCacheRef.current.set(fileId, JSON.parse(JSON.stringify(preloadedTasks)));
        fileOriginalTasksCacheRef.current.set(fileId, JSON.parse(JSON.stringify(preloadedTasks)));
      }
      setLoading(false);
      return;
    }

    const previousFileId = currentFileId;

    // For logged-out users, cache current file's tasks before switching
    if (!isLoggedIn && previousFileId && previousFileId !== fileId) {
      fileTasksCacheRef.current.set(previousFileId, JSON.parse(JSON.stringify(tasks)));
      fileOriginalTasksCacheRef.current.set(previousFileId, JSON.parse(JSON.stringify(originalTasksRef.current)));
    }

    // Mark file as selected immediately; useEffect will load tasks and show loading state
    setCurrentFileId(fileId);

    // Save progress / pending changes in background (logged in only)
    if (isLoggedIn && previousFileId && previousFileId !== 'default' && previousFileId !== fileId) {
      saveFileProgressStatus(previousFileId).catch((err) =>
        console.error('Error saving file progress status:', err)
      );
    }
    if (isLoggedIn) {
      const { modified, created, deleted } = pendingChangesRef.current;
      const hasActualChanges = modified.size > 0 || created.length > 0 || deleted.length > 0;
      if (hasActualChanges) {
        saveAllChanges()
          .then(() => updateLastOpenedFile(fileId))
          .catch((err) => console.error('Error saving changes on file switch:', err));
      } else {
        updateLastOpenedFile(fileId).catch((err) =>
          console.error('Error updating last opened file:', err)
        );
      }
    }
  };

  const showLoggedOutSaveRequired = (kind) => {
    const message =
      kind === 'lists'
        ? `You can create up to ${LOGGED_OUT_MAX_LISTS} lists while logged out. Save & create account to continue.`
        : `You can create up to ${LOGGED_OUT_MAX_TASKS} tasks while logged out. Save & create account to continue.`;
    window.dispatchEvent(
      new CustomEvent('taskdown:save_required', {
        detail: { message },
      })
    );
  };

  const createFileWithLoggedOutLimits = async (name, options = {}) => {
    if (!isLoggedIn) {
      const regularFiles = files.filter((f) => f.id !== 'preset' && !String(f.id).startsWith('preset:'));
      if (regularFiles.length >= LOGGED_OUT_MAX_LISTS) {
        showLoggedOutSaveRequired('lists');
        return null;
      }
    }
    return createFile(name, options);
  };

  const createTaskWithLoggedOutLimits = (...args) => {
    if (!isLoggedIn) {
      const regularFiles = files.filter((f) => f.id !== 'preset' && !String(f.id).startsWith('preset:'));
      let totalTaskCount = 0;
      regularFiles.forEach((file) => {
        const fileTasks = file.id === currentFileId ? tasks : (fileTasksCacheRef.current.get(file.id) || []);
        totalTaskCount += Array.isArray(fileTasks) ? fileTasks.length : 0;
      });
      if (totalTaskCount >= LOGGED_OUT_MAX_TASKS) {
        showLoggedOutSaveRequired('tasks');
        return null;
      }
    }
    return createTask(...args);
  };

  // When user navigated to /list/:id from sidebar (same-tab click), update last_opened_file_id.
  // Do not update when they opened the URL in a new tab (no fromSidebarNavigation).
  useEffect(() => {
    if (
      isLoggedIn &&
      listIdFromUrl != null &&
      fromSidebarNavigation &&
      currentFileId != null &&
      String(currentFileId) === String(listIdFromUrl) &&
      updatedLastOpenedForListIdRef.current !== listIdFromUrl
    ) {
      updatedLastOpenedForListIdRef.current = listIdFromUrl;
      updateLastOpenedFile(listIdFromUrl).catch((err) =>
        console.error('Error updating last opened file:', err)
      );
    }
  }, [isLoggedIn, listIdFromUrl, fromSidebarNavigation, currentFileId, updateLastOpenedFile]);

  // Keep cache updated for logged-out users whenever tasks change.
  // Only cache when tasks actually belong to currentFileId, so we don't overwrite another
  // list's cache when switching (e.g. openExamplePreset) before fetchTasks has loaded the new file.
  useEffect(() => {
    if (!isLoggedIn && currentFileId) {
      const tasksBelongToCurrentFile =
        tasks.length === 0 || tasks.every((t) => t.file_id === currentFileId);
      if (tasksBelongToCurrentFile) {
        fileTasksCacheRef.current.set(currentFileId, JSON.parse(JSON.stringify(tasks)));
        fileOriginalTasksCacheRef.current.set(currentFileId, JSON.parse(JSON.stringify(originalTasksRef.current)));
      }
    }
  }, [tasks, currentFileId, isLoggedIn]);

  // Persist filename and tasks to localStorage when logged out (so edits survive page refresh)
  useEffect(() => {
    if (isLoggedIn || presetSlug || currentFileId == null) return;
    if (currentFileId === 'preset' || String(currentFileId).startsWith('preset:')) return;
    const regularFiles = files.filter((f) => f.id !== 'preset' && !String(f.id).startsWith('preset:'));
    const tasksByFileId = {};
    regularFiles.forEach((file) => {
      const cached = file.id === currentFileId ? tasks : (fileTasksCacheRef.current.get(file.id) || []);
      tasksByFileId[file.id] = JSON.parse(JSON.stringify(cached));
    });
    setLoggedOutWorkspaceDraft({
      files: regularFiles.map((f) => ({ id: f.id, name: f.name, order: f.order, archived: !!f.archived })),
      tasksByFileId,
      currentFileId,
    });
  }, [isLoggedIn, presetSlug, currentFileId, files, tasks]);

  // Clear localStorage draft when user logs in or signs up (session is active)
  useEffect(() => {
    if (isLoggedIn) clearLoggedOutDraft();
  }, [isLoggedIn]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  /** For logged-out users: get cached tasks for an inactive file (e.g. for progress indicator in sidebar). */
  const getCachedTasksForFile = (fileId) => fileTasksCacheRef.current.get(fileId);

  /** For logged-out users: get full workspace snapshot (all files + tasks across files). */
  const getLoggedOutWorkspaceSnapshot = () => {
    const regularFiles = files.filter((f) => f.id !== 'preset' && !String(f.id).startsWith('preset:'));
    const tasksByFileId = {};
    regularFiles.forEach((file) => {
      const fileTasks = file.id === currentFileId ? tasks : (fileTasksCacheRef.current.get(file.id) || []);
      tasksByFileId[file.id] = JSON.parse(JSON.stringify(fileTasks));
    });
    return {
      files: regularFiles.map((f) => ({ id: f.id, name: f.name, order: f.order, archived: !!f.archived })),
      tasksByFileId,
      currentFileId,
    };
  };

  /** Open an example list without changing the URL or adding it to Lists (logged out, on / only). */
  const openExamplePreset = (slug) => {
    if (isLoggedIn || presetSlug) return;
    setCurrentFileId(`preset:${slug}`);
  };

  /** True when logged out and the current file has unsaved changes (used to show Save button only for the active file). */
  const hasPendingChangesForCurrentFile = useMemo(() => {
    if (isLoggedIn || !hasPendingChanges || !currentFileId) return false;
    const { modified, created, deleted } = pendingChangesRef.current;
    if ([...modified.values()].some((t) => t.file_id === currentFileId)) return true;
    if (created.some((t) => t.file_id === currentFileId)) return true;
    if (deleted.some((id) => originalTasksRef.current.find((t) => t.id === id)?.file_id === currentFileId)) return true;
    return false;
  }, [isLoggedIn, hasPendingChanges, currentFileId]);

  return {
    tasks,
    files,
    currentFileId,
    listAccessDenied,
    presetSlug,
    loading,
    hasPendingChanges,
    hasPendingChangesForCurrentFile,
    hasRestoredDraft,
    fetchTasks,
    fetchFiles,
    createFile: createFileWithLoggedOutLimits,
    switchFile,
    updateFileName,
    toggleArchiveFile,
    updateFileOrders,
    updateFileArchiveStatus,
    updateTaskStatus,
    updateTaskText,
    updateTaskTextLocal,
    updateTaskDueDate,
    updateTaskOrder,
    updateTaskOrders,
    updateTaskParent,
    updateTaskParentWithText,
    updateTaskParentWithTextAndOrder,
    createTask: createTaskWithLoggedOutLimits,
    deleteTask,
    clearAllTasks,
    saveAllChanges,
    getCachedTasksForFile,
    getLoggedOutWorkspaceSnapshot,
    openExamplePreset
  };
}

